import React, { useState } from "react"
import {
  Modal, Upload, Button, Alert, Spin, message,
} from "antd"
import { DownloadOutlined } from "@ant-design/icons"
import csvuploadwhiteIcon from "../../../assets/images/export-white.png"
import csvuploadIcon from "../../../assets/images/export.png"

const { Dragger } = Upload

const VALID_DOMAINS = [
  "Instructional Practices",
  "Social Emotional Supports",
  "Behavior Supports",
  "Extracurricular Activities",
  "Adult SEL",
]

function buildGradesForItem(row, allowedGrades, gradePopulations = {}) {
  const tier = (row["Tier of Support"] || "").trim()
  const domain = (row.Domain || "").trim()
  const gradeStr = (row["Grade Levels Supported"] || "").trim()
  const studentsCanStr = (
    row["If Targeted/Tertiary, # students can be supported"] || ""
  ).trim()
  const studentsBeingStr = (
    row["If Targeted/Tertiary, # students who are being supported"] || ""
  ).trim()

  if (domain.toLowerCase() === "adult sel") return []

  const grades = gradeStr
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean)

  // Only keep grades that are configured for this school
  const filteredGrades = allowedGrades && allowedGrades.length > 0
    ? grades.filter((g) => allowedGrades.includes(g))
    : grades

  if (tier === "Universal") {
    return filteredGrades.map((grade) => ({
      grade,
      students_can_be_supported: null,
      students_being_supported: null,
    }))
  }

  if (tier === "Targeted" || tier === "Tertiary") {
    const canMissing = !studentsCanStr
    const canParsed = studentsCanStr.split(",").map((s) => parseInt(s.trim(), 10))
    const canArr = canParsed.map((v) => (Number.isNaN(v) ? 0 : v))
    const beingArr = studentsBeingStr
      .split(",")
      .map((s) => parseInt(s.trim(), 10) || 0)
    return filteredGrades.map((grade) => {
      const originalIndex = grades.indexOf(grade)
      const population = gradePopulations[grade]
      let canVal
      if (canMissing) {
        // Column empty → default to student population so the form pre-fills correctly
        canVal = population || 0
      } else {
        const rawCan = originalIndex < canArr.length ? (canArr[originalIndex] || 0) : (population || 0)
        canVal = population !== undefined && rawCan > population ? population : rawCan
      }
      const beingVal = beingArr[originalIndex] || 0
      return {
        grade,
        students_can_be_supported: canVal,
        students_being_supported: beingVal > canVal ? 0 : beingVal,
      }
    })
  }

  return []
}

function downloadSampleXLSX(grades) {
  const link = document.createElement("a")
  let url = "/api/v1/program_matrix/program_matrix_items/download_xlsx_template"
  if (grades && grades.length > 0) {
    const gradeKeys = grades.map((g) => g.grade).join(",")
    const populations = grades.map((g) => g.student_population || 0).join(",")
    url += `?grades=${encodeURIComponent(
      gradeKeys,
    )}&populations=${encodeURIComponent(populations)}`
  }
  link.href = url
  link.download = "program_matrix_sample.xlsx"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function isRowComplete(row, { studentCountsDefaulted = false } = {}) {
  const activityName = (row["Activity Name"] || "").trim()
  const tier = (row["Tier of Support"] || "").trim()
  const domain = (row.Domain || "").trim()
  const length = (row["Length of time implemented at school"] || "").trim()
  const description = (row.Description || "").trim()
  const sel = (row["SEL indicator"] || "").trim()
  const promotes = (row["Promotes or Reinforces"] || "promotes").trim()
  const describeHow = (
    row["Describe how this activity promotes/reinforces this"] || ""
  ).trim()

  if (
    !activityName
    || !tier
    || !domain
    || !length
    || !description
    || !sel
    || !promotes
    || !describeHow
  ) return false

  const isTargetedOrTertiary = tier === "Targeted" || tier === "Tertiary"
  const isAdultSel = domain.toLowerCase() === "adult sel"
  if (!isAdultSel && !(row["Grade Levels Supported"] || "").trim()) return false
  if (isTargetedOrTertiary && !isAdultSel) {
    if (!(row["Grade Levels Supported"] || "").trim()) return false
    // If student counts were missing but defaulted from school population, treat as present
    if (!studentCountsDefaulted) {
      if (
        !(row["If Targeted/Tertiary, # students can be supported"] || "").trim()
      ) return false
      if (
        !(
          row["If Targeted/Tertiary, # students who are being supported"] || ""
        ).trim()
      ) return false
    }
  }

  return true
}

function computeRowIssues(row, index, allowedGrades, gradePopulations = {}) {
  const issues = []
  const invalidFields = row._invalid_fields || {}
  const domain = (row.Domain || "").trim()
  const tier = (row["Tier of Support"] || "").trim()
  const activityName = (row["Activity Name"] || "").trim()

  // Domain issues are fatal — the row will be skipped
  if (!domain) {
    if (invalidFields.Domain) {
      issues.push({
        field: "Domain",
        type: "invalid",
        value: invalidFields.Domain,
        fatal: true,
      })
    } else {
      issues.push({ field: "Domain", type: "missing", fatal: true })
    }
  }

  const fieldsToCheck = [
    ["Activity Name", "Activity Name"],
    ["Tier of Support", "Tier of Support"],
    ["Length of time implemented at school", "Length of Time"],
    ["Description", "Description"],
    ["SEL indicator", "SEL Indicator"],
    ["Promotes or Reinforces", "Promotes or Reinforces"],
    [
      "Describe how this activity promotes/reinforces this",
      "Describe how this activity promotes/reinforces this",
    ],
  ]

  fieldsToCheck.forEach(([rowKey, label]) => {
    if (invalidFields[rowKey]) {
      issues.push({
        field: label,
        type: "invalid",
        value: invalidFields[rowKey],
      })
    } else if (!(row[rowKey] || "").trim()) {
      issues.push({ field: label, type: "missing" })
    }
  })

  if (tier === "Universal" && domain.toLowerCase() !== "adult sel") {
    if (!(row["Grade Levels Supported"] || "").trim()) {
      issues.push({ field: "Grade Levels Supported", type: "missing" })
    }
  }

  if (
    (tier === "Targeted" || tier === "Tertiary")
    && domain.toLowerCase() !== "adult sel"
  ) {
    if (!(row["Grade Levels Supported"] || "").trim()) {
      issues.push({ field: "Grade Levels Supported", type: "missing" })
    }

    const gradeStrForDefault = (row["Grade Levels Supported"] || "").trim()
    const matchedGrades = gradeStrForDefault
      .split(",")
      .map((g) => g.trim())
      .filter((g) => g && allowedGrades && allowedGrades.includes(g))

    const studentsCanStr = (
      row["If Targeted/Tertiary, # students can be supported"] || ""
    ).trim()
    if (!studentsCanStr) {
      if (matchedGrades.length > 0) {
        const defaultSummary = matchedGrades
          .map((g) => `${g}: ${gradePopulations[g] || 0}`)
          .join(", ")
        issues.push({
          field: "# students can be supported",
          type: "missing",
          message: `# students can be supported: missing (defaulted to student population — ${defaultSummary})`,
        })
      } else {
        issues.push({ field: "# students can be supported", type: "missing" })
      }
    } else {
      studentsCanStr.split(",").forEach((val) => {
        const v = val.trim()
        if (v && !/^\d+$/.test(v)) {
          issues.push({
            field: "# students can be supported",
            type: "invalid",
            value: v,
            message: `# students can be supported: invalid number "${v}" — will be uploaded as 0`,
          })
        }
      })
    }

    const studentsBeingStr = (
      row["If Targeted/Tertiary, # students who are being supported"] || ""
    ).trim()
    if (!studentsBeingStr) {
      if (matchedGrades.length > 0) {
        issues.push({
          field: "# students being supported",
          type: "missing",
          message: "# students being supported: missing (defaulted to 0 for all grades)",
        })
      } else {
        issues.push({ field: "# students being supported", type: "missing" })
      }
    } else {
      studentsBeingStr.split(",").forEach((val) => {
        const v = val.trim()
        if (v && !/^\d+$/.test(v)) {
          issues.push({
            field: "# students being supported",
            type: "invalid",
            value: v,
            message: `# students being supported: invalid number "${v}" — will be uploaded as 0`,
          })
        }
      })
    }

    // Check being > can per grade
    const gradeStr = (row["Grade Levels Supported"] || "").trim()
    if (gradeStr && studentsCanStr && studentsBeingStr) {
      const allGrades = gradeStr
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
      const gradesForCheck = allowedGrades && allowedGrades.length > 0
        ? allGrades.filter((g) => allowedGrades.includes(g))
        : allGrades
      const canArr = studentsCanStr.split(",").map((s) => s.trim())
      const beingArr = studentsBeingStr.split(",").map((s) => s.trim())

      gradesForCheck.forEach((grade) => {
        const originalIndex = allGrades.indexOf(grade)
        const rawCanVal = parseInt(canArr[originalIndex], 10)
        const population = gradePopulations[grade]
        const canExceedsPopulation = !Number.isNaN(rawCanVal)
          && population !== undefined
          && rawCanVal > population
        if (canExceedsPopulation) {
          issues.push({
            field: "# students can be supported",
            type: "invalid",
            value: rawCanVal,
            message: `Grade ${grade}: students can be supported (${rawCanVal}) exceeds the school population (${population}) — will be uploaded as ${population}`,
          })
        }
        const canVal = canExceedsPopulation ? population : rawCanVal
        const beingVal = parseInt(beingArr[originalIndex], 10)
        if (
          !Number.isNaN(canVal)
          && !Number.isNaN(beingVal)
          && beingVal > canVal
        ) {
          issues.push({
            field: "# students being supported",
            type: "invalid",
            value: beingVal,
            message: `Grade ${grade}: students being supported (${beingVal}) cannot exceed students can be supported (${canVal}) — will be uploaded as 0`,
          })
        }
      })
    }
  }

  // Warn when fewer population values are provided than valid grades
  if (
    (tier === "Targeted" || tier === "Tertiary")
    && domain.toLowerCase() !== "adult sel"
  ) {
    const gradeStr2 = (row["Grade Levels Supported"] || "").trim()
    const studentsCanStr2 = (row["If Targeted/Tertiary, # students can be supported"] || "").trim()
    if (gradeStr2 && studentsCanStr2) {
      const allGradesArr = gradeStr2.split(",").map((g) => g.trim()).filter(Boolean)
      const validGradesArr = allowedGrades && allowedGrades.length > 0
        ? allGradesArr.filter((g) => allowedGrades.includes(g))
        : allGradesArr
      const canValArr = studentsCanStr2.split(",").map((v) => v.trim()).filter(Boolean)
      const gradesWithMissingCan = validGradesArr.filter(
        (g) => allGradesArr.indexOf(g) >= canValArr.length,
      )
      if (gradesWithMissingCan.length > 0) {
        issues.push({
          field: "# students can be supported",
          type: "grade_count_mismatch",
          message: `Population data missing for grades ${gradesWithMissingCan.join(", ")}. Default values will be used: the student population for each grade for # students who can be supported and 0 for # students being supported.`,
        })
      }
    }
  }

  // Grade level overlap check (only for non-Adult SEL rows with grade data)
  if (
    domain
    && domain.toLowerCase() !== "adult sel"
    && allowedGrades
    && allowedGrades.length > 0
  ) {
    const gradeStr = (row["Grade Levels Supported"] || "").trim()
    if (gradeStr) {
      const fileGrades = gradeStr
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
      if (fileGrades.length > 0) {
        const matching = fileGrades.filter((g) => allowedGrades.includes(g))
        const nonMatching = fileGrades.filter(
          (g) => !allowedGrades.includes(g),
        )
        if (matching.length === 0) {
          issues.push({
            field: "Grade Levels Supported",
            type: "grade_mismatch",
            message: `Grade Levels (${fileGrades.join(
              ", ",
            )}) do not match any school-configured grades (${allowedGrades.join(
              ", ",
            )}). This row will be uploaded with no grade data.`,
          })
        } else if (nonMatching.length > 0) {
          issues.push({
            field: "Grade Levels Supported",
            type: "grade_partial",
            message: `Grade Levels ${nonMatching.join(
              ", ",
            )} are not configured for this school and will be excluded. Only ${matching.join(
              ", ",
            )} will be uploaded.`,
          })
        }
      }
    }
  }

  const rowLabel = activityName
    ? `Row ${index + 2} ("${activityName}")`
    : `Row ${index + 2}`
  const rowNumber = `Row ${index + 2}`
  const willBeSkipped = issues.some((i) => i.fatal)

  return {
    rowLabel, rowNumber, issues, willBeSkipped,
  }
}

function EnhancedCSVUploadModal({
  onUploadSuccess,
  selectedGrades = [],
  disabled = false,
}) {
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [parsedRows, setParsedRows] = useState([])
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fileList, setFileList] = useState([])
  const [uploadErrors, setUploadErrors] = useState([])
  const [issuesModalOpen, setIssuesModalOpen] = useState(false)
  const [rowIssues, setRowIssues] = useState([])

  const handleUploadFile = async (rowsToUpload) => {
    const rows = rowsToUpload || parsedRows
    setUploading(true)
    setUploadErrors([])

    const rowErrors = []

    await Promise.all(
      rows.map(async (row, index) => {
        const activityName = (row["Activity Name"] || "").trim()
        const rowLabel = activityName
          ? `Row ${index + 2} ("${activityName}")`
          : `Row ${index + 2}`
        const category = (row.Domain || "").trim()

        if (!category || !VALID_DOMAINS.includes(category)) return

        const selIndicator = (row["SEL indicator"] || "").trim()
        const rawConnection = (row["Promotes or Reinforces"] || "")
          .trim()
          .toLowerCase()
        const connectionType = rawConnection === "reinforces" ? "reinforces" : "promotes"
        const connectionExplanation = (
          row["Describe how this activity promotes/reinforces this"] || ""
        ).trim()

        const result = selIndicator.match(/^(\d[A-Z])/)
        const selKey = result ? result[1] : selIndicator
        const selStandards = selKey
          ? {
            [selKey]: {
              connection_type: connectionType,
              ...(connectionExplanation && {
                connection_explanation: connectionExplanation,
              }),
            },
          }
          : {}

        try {
          const createResponse = await new Promise((resolve, reject) => {
            APIClient.post(
              "/program_matrix/program_matrix_items/",
              { category },
              resolve,
              reject,
            )
          })

          const itemId = createResponse.id
          if (!itemId) {
            rowErrors.push(
              `${rowLabel}: Could not create activity — Domain "${category}" is not a valid option.`,
            )
            return
          }

          const allowedGrades = selectedGrades.map((g) => g.grade)
          const gradePopulations = Object.fromEntries(
            selectedGrades.map((g) => [
              g.grade,
              parseInt(g.student_population, 10) || 0,
            ]),
          )
          const internalSelectedGrades = buildGradesForItem(
            row,
            allowedGrades,
            gradePopulations,
          )
          // CSV uploads always set is_user_edited=true so the form shows exactly what
          // was in the file (empty or not) and never auto-fills with all school grades
          const isUserEdited = true

          const rawTier = (row["Tier of Support"] || "").trim()
          const isTargetedOrTertiary = rawTier === "Targeted" || rawTier === "Tertiary"
          const studentsCanMissing = !(row["If Targeted/Tertiary, # students can be supported"] || "").trim()
          const studentsBeingMissing = !(row["If Targeted/Tertiary, # students who are being supported"] || "").trim()
          // Counts are "defaulted" when grades matched school grades but counts were empty in file
          const studentCountsDefaulted = isTargetedOrTertiary
            && (studentsCanMissing || studentsBeingMissing)
            && internalSelectedGrades.length > 0

          await new Promise((resolve, reject) => {
            APIClient.put(
              `/program_matrix/program_matrix_items/${itemId}`,
              {
                activity: activityName,
                tier_of_support: rawTier,
                length_of_time: (
                  row["Length of time implemented at school"] || ""
                ).trim(),
                description: (row.Description || "").trim(),
                data_collection: (row["Data being collected"] || "").trim(),
                sel_standards: selStandards,
                is_completed: isRowComplete(row, { studentCountsDefaulted }),
                category,
                internal_selected_grades: internalSelectedGrades,
                is_user_edited: isUserEdited,
              },
              resolve,
              reject,
            )
          })
        } catch (err) {
          const messages = (err && err.errors)
            || (err && err.message ? [err.message] : ["Unknown error"])
          messages.forEach((msg) => rowErrors.push(`${rowLabel}: ${msg}`))
        }
      }),
    )

    setUploading(false)

    if (rowErrors.length > 0) {
      setUploadErrors(rowErrors)
      // Open the issues modal to surface upload errors if it isn't already open
      if (!issuesModalOpen) setIssuesModalOpen(true)
      return
    }

    setIssuesModalOpen(false)
    setUploadModalOpen(false)
    setParsedRows([])
    setFileList([])
    if (onUploadSuccess) onUploadSuccess()

    message.success("Activities uploaded successfully!")
    setTimeout(() => {
      window.location.href = "/program_matrices"
    }, 1500)
  }

  const processFile = async (file) => {
    setLoading(true)
    setValidationErrors([])

    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(
            new Error("Request timeout. The file may be empty or corrupted."),
          )
        }, 30000)

        APIClient.post(
          "/program_matrix/program_matrix_items/preview_csv",
          formData,
          (res) => {
            clearTimeout(timeout)
            resolve(res)
          },
          (error) => {
            clearTimeout(timeout)
            reject(error)
          },
        )
      })

      if (response.success && response.data && response.data.length > 0) {
        const rows = response.data
        setParsedRows(rows)

        const allowedGrades = selectedGrades.map((g) => g.grade)
        const gradePopulations = Object.fromEntries(
          selectedGrades.map((g) => [
            g.grade,
            parseInt(g.student_population, 10) || 0,
          ]),
        )
        const issues = rows
          .map((row, i) => computeRowIssues(row, i, allowedGrades, gradePopulations))
          .filter((r) => r.issues.length > 0)

        if (issues.length === 0) {
          // All rows valid — upload immediately (loading spinner stays visible in upload modal)
          await handleUploadFile(rows)
        } else {
          setRowIssues(issues)
          setUploadModalOpen(false)
          setIssuesModalOpen(true)
        }
      } else if (
        response.success
        && response.data
        && response.data.length === 0
      ) {
        setValidationErrors([
          "The uploaded file is empty or contains no data rows.",
        ])
        setParsedRows([])
      } else {
        setValidationErrors(response.errors || ["Validation failed"])
        setParsedRows([])
      }
    } catch (error) {
      if (error && error.errors) {
        if (error.errors.length > 5) {
          setValidationErrors([
            "Invalid file format. Please ensure the file content matches the expected structure.",
          ])
        } else {
          setValidationErrors(error.errors)
        }
      } else if (error && error.message) {
        setValidationErrors([error.message])
      } else {
        setValidationErrors([
          "An unexpected error occurred during file processing.",
        ])
      }
      setParsedRows([])
    } finally {
      setLoading(false)
    }
  }

  const handleOpenUploadModal = () => {
    setValidationErrors([])
    setParsedRows([])
    setFileList([])
    setUploadModalOpen(true)
  }

  const skippedIssues = rowIssues.filter((r) => r.willBeSkipped)
  // Upload Anyway is only available when there are rows with valid domains to upload
  const canUploadAny = parsedRows.length > skippedIssues.length

  return (
    <>
      <Button
        style={{
          backgroundColor: disabled ? "#d9d9d9" : "#3ea4a5",
          borderColor: disabled ? "#d9d9d9" : "#3ea4a5",
          color: "white",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          fontWeight: 400,
        }}
        icon={(
          <img
            src={csvuploadwhiteIcon}
            className="csv-upload-modal__file-icon"
            style={{ width: "16px", height: "16px", marginRight: "8px" }}
          />
        )}
        onClick={handleOpenUploadModal}
        disabled={disabled}
        title={
          disabled
            ? "Please configure School Grade Levels and populations first"
            : ""
        }
      >
        Upload File
      </Button>

      {/* Upload modal */}
      <Modal
        className="program-matrix-upload-modal"
        centered
        title={(
          <div>
            <div style={{ fontWeight: 600, fontSize: 18, color: "#575555" }}>
              Upload Program Matrix File
            </div>
            <div
              style={{
                fontWeight: 400,
                fontSize: 14,
                color: "#768492",
                marginTop: 4,
              }}
            >
              Review the parsed indicator data below. Upload a file or use the
              sample format to get started.
            </div>
          </div>
        )}
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false)
          setValidationErrors([])
          setFileList([])
        }}
        footer={[
          <Button
            key="upload-file-btn"
            type="primary"
            icon={(
              <img
                src={csvuploadwhiteIcon}
                className="csv-upload-modal__file-icon"
                style={{ width: "16px", height: "16px", marginRight: "8px" }}
              />
            )}
            loading={loading}
            disabled={
              loading || fileList.length === 0 || validationErrors.length > 0
            }
            style={{
              backgroundColor: "#3ea4a5",
              borderColor: "#3ea4a5",
              borderRadius: 6,
              color: "white",
            }}
            onClick={() => {
              if (fileList.length > 0) processFile(fileList[0])
            }}
          >
            {loading ? "Processing…" : "Upload File"}
          </Button>,
        ]}
        width={650}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 16,
              color: "#575555",
              fontFamily: "Archivo Narrow",
            }}
          >
            Upload Program Matrix
          </span>
          <Button
            type="link"
            icon={<DownloadOutlined />}
            style={{
              color: "#0CA4A5",
              fontWeight: 500,
              padding: 0,
              fontSize: "16px",
            }}
            onClick={() => downloadSampleXLSX(selectedGrades)}
          >
            Download Sample XLSX
          </Button>
        </div>

        <Dragger
          name="file"
          accept=".csv,.xlsx,.xls"
          multiple={false}
          fileList={fileList}
          showUploadList={false}
          beforeUpload={(file) => {
            setValidationErrors([])
            setParsedRows([])
            setFileList([file])
            return false
          }}
          onRemove={() => {
            setFileList([])
            setValidationErrors([])
            setParsedRows([])
          }}
          style={{ borderRadius: 8 }}
          disabled={loading}
        >
          {loading ? (
            <div style={{ padding: "40px" }}>
              <Spin size="large" tip="Processing file…" />
            </div>
          ) : fileList.length > 0 ? (
            <>
              <p className="ant-upload-drag-icon">
                <img
                  src={csvuploadIcon}
                  className="csv-upload-modal__file-icon"
                />
              </p>
              <p
                style={{
                  fontWeight: 500,
                  color: "#666",
                  marginBottom: 4,
                  fontSize: "14px",
                }}
              >
                {fileList[0].name}
              </p>
              <p style={{ color: "#aaa", fontSize: 13, margin: 0 }}>
                Click or drag file to this area to replace
              </p>
            </>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <img
                  src={csvuploadIcon}
                  className="csv-upload-modal__file-icon"
                />
              </p>
              <p
                style={{
                  fontWeight: 500,
                  color: "#666",
                  marginBottom: 4,
                  fontSize: "14px",
                }}
              >
                Upload Program Matrix
              </p>
              <p style={{ color: "#aaa", fontSize: 13, margin: 0 }}>
                CSV or XLSX (Max size 10MB)
              </p>
            </>
          )}
        </Dragger>

        {validationErrors.length > 0 && (
          <div
            style={{
              marginTop: 12,
              maxHeight: "250px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            <Alert
              type="error"
              message="Validation Errors"
              description={(
                <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                  {validationErrors.map((err, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {err}
                    </li>
                  ))}
                </ul>
              )}
              showIcon
            />
          </div>
        )}
      </Modal>

      {/* Issues modal — replaces preview modal and domain-warning modal */}
      <Modal
        className="program-matrix-issues-modal"
        centered
        closable={false}
        open={issuesModalOpen}
        width={580}
        title={(
          <div>
            <div className="csv-issues-modal__title">
              File contains issues
            </div>
            <div className="csv-issues-modal__subtitle">
              {canUploadAny
                ? "Some rows have missing or invalid data. Rows with an invalid Domain will be skipped."
                : "All rows have a missing or invalid Domain. Please fix the Domain values before uploading."}
            </div>
          </div>
        )}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIssuesModalOpen(false)
              setUploadErrors([])
            }}
            className="csv-issues-modal__cancel-btn"
          >
            Cancel
          </Button>,
          ...(canUploadAny
            ? [
              <Button
                key="upload-anyway"
                loading={uploading}
                onClick={() => handleUploadFile(parsedRows)}
                className="csv-issues-modal__upload-btn"
              >
                Upload Anyway
              </Button>,
            ]
            : []),
        ]}
      >
        {uploadErrors.length > 0 && (
          <Alert
            type="error"
            message="Upload Errors"
            description={(
              <ul className="csv-issues-modal__error-list">
                {uploadErrors.map((err, i) => (
                  <li key={i} className="csv-issues-modal__error-item">
                    {err}
                  </li>
                ))}
              </ul>
            )}
            showIcon
            className="csv-issues-modal__upload-errors"
          />
        )}

        {skippedIssues.length > 0 && (
          <div className="csv-issues-modal__skipped-banner">
            Rows with an invalid Domain cannot be uploaded:&nbsp;
            {skippedIssues.map((r) => r.rowNumber).join(", ")}
          </div>
        )}

        {rowIssues.some((r) => !r.willBeSkipped) && (
          <div className="csv-issues-modal__incomplete-banner">
            The following rows will be uploaded as Incomplete (missing or invalid data)
          </div>
        )}

        {rowIssues.map(({ rowLabel, issues, willBeSkipped }) => {
          const headerClass = `csv-issues-modal__field-header${willBeSkipped ? " csv-issues-modal__field-header--fatal" : ""}`
          return (
            <div key={rowLabel} className="csv-issues-modal__field-group">
              <div className={headerClass}>{rowLabel}</div>
              <ul className="csv-issues-modal__field-issues">
                {issues.map((issue, j) => {
                  let itemClass = "csv-issues-modal__issue-item"
                  if (issue.fatal || issue.type === "invalid") {
                    itemClass += " csv-issues-modal__issue-item--invalid"
                  } else if (issue.type === "grade_mismatch") {
                    itemClass += " csv-issues-modal__issue-item--mismatch"
                  } else if (issue.type === "grade_partial") {
                    itemClass += " csv-issues-modal__issue-item--partial"
                  }
                  const text = issue.message || `${issue.field}: ${issue.type === "invalid" ? "invalid value" : "missing"}`
                  return (
                    <li key={j} className={itemClass}>{text}</li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </Modal>
    </>
  )
}

export default EnhancedCSVUploadModal
